import { Fragment, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '@/services';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

interface DashboardTopAdHocItemsCardProps {
  companyId: string;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export function DashboardTopAdHocItemsCard({ companyId }: DashboardTopAdHocItemsCardProps) {
  const { data: items, isLoading } = useQuery({
    queryKey: ['dashboard-top-adhoc-items', companyId],
    queryFn: async () => {
      const result = await dashboardService.getTopAdHocItems(companyId);
      return result.data || [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId,
  });

  const topItems = useMemo(() => {
    if (!items || items.length === 0) return [];

    // Group by (description, vendorName)
    const groups = new Map<string, { count: number; totalPrice: number; description: string; vendorName: string }>();

    items.forEach((item) => {
      const desc = item.description || 'No description';
      const vendor = item.vendor_name;
      const key = `${desc}|||${vendor}`;

      const existing = groups.get(key);
      if (existing) {
        existing.count++;
        existing.totalPrice += item.unit_price || 0;
      } else {
        groups.set(key, {
          count: 1,
          totalPrice: item.unit_price || 0,
          description: desc,
          vendorName: vendor,
        });
      }
    });

    return Array.from(groups.values())
      .map((g) => ({
        ...g,
        avgPrice: g.count > 0 ? g.totalPrice / g.count : 0,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [items]);

  return (
    <>
      <CardHeader className="pb-2">
        <CardTitle>Top Ad-Hoc Items</CardTitle>
      </CardHeader>
      <CardContent className="p-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : topItems.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right"># of Uses</TableHead>
                <TableHead className="text-right">Avg Price</TableHead>
                <TableHead>Item Name</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topItems.map((item) => (
                <Fragment key={`${item.description}|||${item.vendorName}`}>
                  <TableRow className="border-b-0">
                    <TableCell colSpan={3} className="text-xs text-muted-foreground pb-0">
                      {item.vendorName}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-right font-medium">{item.count}</TableCell>
                    <TableCell className="text-right">
                      {currencyFormatter.format(item.avgPrice)}
                    </TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate" title={item.description}>
                      {item.description}
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground p-6">
            No ad-hoc items found
          </div>
        )}
      </CardContent>
    </>
  );
}
